import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch, apiJson } from '@/lib/api/client'

interface Organization {
  id: string
  name: string
  display_name: string | null
  created_at: string
}

export const Route = createFileRoute('/_app/admin/organizations')({
  component: PlatformAdminOrgsPage,
})

function PlatformAdminOrgsPage() {
  const qc = useQueryClient()
  const [slug, setSlug] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const orgs = useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: () => apiJson<Organization[]>('/api/admin/organizations'),
  })

  const create = useMutation({
    mutationFn: async () => {
      setError(null)
      const r = await apiFetch('/api/admin/organizations', {
        method: 'POST',
        body: JSON.stringify({
          slug: slug.trim(),
          display_name: displayName.trim() || null,
        }),
      })
      if (!r.ok) throw new Error(await r.text())
    },
    onSuccess: () => {
      setSlug('')
      setDisplayName('')
      qc.invalidateQueries({ queryKey: ['admin', 'organizations'] })
      qc.invalidateQueries({ queryKey: ['auth', 'session'] })
    },
    onError: (e: Error) => setError(e.message),
  })

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const r = await apiFetch(`/api/admin/organizations/${id}`, {
        method: 'DELETE',
      })
      if (!r.ok) throw new Error(await r.text())
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'organizations'] })
      qc.invalidateQueries({ queryKey: ['auth', 'session'] })
    },
  })

  return (
    <div className="p-8 space-y-8 max-w-3xl">
      <h1 className="text-2xl font-medium tracking-tight">Organizations</h1>

      <section className="space-y-3 border border-border rounded-lg p-5">
        <h2 className="text-sm font-medium">Create organization</h2>
        <div className="grid grid-cols-2 gap-3">
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            placeholder="slug (a-z, 0-9, -)"
            className="h-9 px-3 rounded-md bg-secondary border border-border font-mono text-sm"
          />
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Display name (optional)"
            className="h-9 px-3 rounded-md bg-secondary border border-border text-sm"
          />
        </div>
        {error && (
          <p className="text-xs text-destructive font-mono">{error}</p>
        )}
        <button
          type="button"
          onClick={() => create.mutate()}
          disabled={create.isPending || !slug.trim()}
          className="h-8 px-4 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
        >
          {create.isPending ? 'Creating…' : 'Create org'}
        </button>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-medium">All organizations</h2>
        {orgs.isLoading && (
          <p className="text-xs text-muted-foreground">Loading…</p>
        )}
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground border-b border-border">
            <tr>
              <th className="text-left font-medium py-2">Slug</th>
              <th className="text-left font-medium py-2">Display name</th>
              <th className="text-left font-medium py-2">Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {orgs.data?.map((o) => (
              <tr key={o.id} className="border-b border-border/40">
                <td className="py-2 font-mono">{o.name}</td>
                <td className="py-2">{o.display_name ?? '—'}</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">
                  {new Date(o.created_at).toLocaleDateString()}
                </td>
                <td className="py-2 text-right">
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Delete org '${o.name}'? This removes all repositories, packages, policies, and PATs owned by it.`,
                        )
                      ) {
                        remove.mutate(o.id)
                      }
                    }}
                    className="text-xs text-destructive hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  )
}
